import React from "react";
import { useTranslation } from "react-i18next";
import { outcomeMetrics } from "@/lib/decisionGraph";
import { getLegend, CATEGORIES } from "@/lib/decisionGraphLegend";
import SolverComparison from "./SolverComparison";
import QuantumContributionPanel from "./QuantumContributionPanel";
import RealizedOutcomePanel from "./RealizedOutcomePanel";
import AgentClusterInspector from "./AgentClusterInspector";

export default function EvidenceRail({
  recon, summary, quantum, integrityOk, evidence, verdict, legendVersion,
  clusters, selectedClusterId, clusterExpanded, onSelectCluster, onCloseCluster,
}) {
  const { t } = useTranslation();
  const metrics = outcomeMetrics(summary);
  const approvals = recon?.approvals || [];
  const legend = getLegend(legendVersion);
  const rendererLabel = verdict.supported
    ? (verdict.software ? t("decisionGraph.rail.rendererSoftware") : t("decisionGraph.rail.rendererWebgl2"))
    : t("decisionGraph.rail.rendererFallback", { reason: verdict.reason });

  return (
    <div className="p-4 space-y-5" data-testid="dg-evidence-rail">
      <Section title={`${t("decisionGraph.legend.title")} · ${t("decisionGraph.legend.version", { v: legend.version })}`}>
        <div className="grid grid-cols-1 gap-1" data-testid="dg-legend">
          {CATEGORIES.map((c) => (
            <div key={c} className="flex items-center gap-2 text-[11px] text-[var(--qn-foreground-muted)]">
              <span className="w-3 h-3 rounded-sm border border-[var(--qn-border)]" style={{ backgroundColor: legend.colors[c] }} />
              {t(`decisionGraph.legend.${c}`)}
            </div>
          ))}
        </div>
      </Section>

      <Section title={t("decisionGraph.rail.outcome")}>
        <div className="grid grid-cols-2 gap-2">
          {metrics.map((m) => (
            <div key={m.label} className="rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-[var(--qn-foreground-subtle)]">{railLabel(t, m.label)}</div>
              <div className="text-sm font-semibold text-[var(--qn-foreground)]">{m.value === "Unavailable" ? t("decisionGraph.rail.unavailable") : String(m.value)}</div>
            </div>
          ))}
        </div>
        {!quantum && (
          <p className="text-[11px] text-[var(--qn-foreground-subtle)] mt-2" data-testid="dg-no-quantum-explainer">
            {t("decisionGraph.rail.noQuantumExplainer")}
          </p>
        )}
      </Section>

      <SolverComparison treatments={summary?.treatments} />
      <RealizedOutcomePanel summary={summary} evidence={evidence} />
      <QuantumContributionPanel summary={summary} evidence={evidence} />

      <AgentClusterInspector clusters={clusters} selectedId={selectedClusterId}
        expanded={clusterExpanded} onSelect={onSelectCluster} onClose={onCloseCluster} />

      <Section title={t("decisionGraph.rail.evidence", { count: evidence.length })}>
        {evidence.length === 0 && <Empty>{t("decisionGraph.rail.noEvidence")}</Empty>}
        {evidence.map((e, i) => (
          <div key={e.evidence_id || i} className="rounded-lg border border-[var(--qn-border)] px-3 py-2 mb-2" data-testid={`dg-evidence-${i}`}>
            <div className="flex items-center justify-between">
              <span className={`text-xs font-semibold ${e.status === "verified" ? "text-[var(--qn-success)]" : "text-[var(--qn-warning)]"}`}>{e.status}</span>
              <span className="text-[10px] text-[var(--qn-foreground-subtle)]">{e.confidence || t("decisionGraph.rail.confidenceUnavailable")}</span>
            </div>
            <div className="text-[11px] text-[var(--qn-foreground-muted)] mt-1">
              {(e.producing_activity || t("decisionGraph.rail.activityUnavailable"))} · {e.verifier || t("decisionGraph.rail.verifierUnavailable")}
            </div>
          </div>
        ))}
      </Section>

      <Section title={t("decisionGraph.rail.approvals", { count: approvals.length })}>
        {approvals.length === 0 && <Empty>{t("decisionGraph.rail.noApprovals")}</Empty>}
        {approvals.map((a, i) => (
          <div key={a.approval_id || i} className="rounded-lg border border-[var(--qn-border)] px-3 py-2 mb-2" data-testid={`dg-approval-${i}`}>
            <div className="text-xs text-[var(--qn-foreground)]">{a.purpose || a.resource_kind || "approval"}</div>
            <div className="text-[11px] text-[var(--qn-foreground-subtle)] mt-0.5">{t("decisionGraph.rail.acceptedBy", { approver: a.approver || t("decisionGraph.rail.unavailable") })}</div>
          </div>
        ))}
        <p className="text-[10px] text-[var(--qn-foreground-subtle)] mt-1" data-testid="dg-human-accepted">{t("decisionGraph.rail.humanAccepted")}</p>
      </Section>

      <Section title={t("decisionGraph.rail.integrity")}>
        <div className="text-xs text-[var(--qn-foreground-muted)]">
          {t("decisionGraph.rail.eventChain")}: {recon ? (integrityOk ? t("decisionGraph.rail.verified") : t("decisionGraph.rail.unverified")) : t("decisionGraph.rail.unavailable")}
        </div>
        <div className="text-[11px] text-[var(--qn-foreground-subtle)] mt-1">{t("decisionGraph.rail.renderer")}: {rendererLabel}</div>
      </Section>
    </div>
  );
}

function railLabel(t, label) {
  const map = {
    "Engine class": "engineClass", "Selected engine": "selectedEngine",
    "Candidates compared": "candidatesCompared", "Agents mobilized": "agentsMobilized",
  };
  return t(`decisionGraph.rail.${map[label] || "unavailable"}`);
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--qn-foreground-subtle)] font-semibold mb-2">{title}</div>
      {children}
    </div>
  );
}
function Empty({ children }) {
  return <div className="text-[11px] text-[var(--qn-foreground-subtle)] italic">{children}</div>;
}
